import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import type { ReactNode } from 'react';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { auth, firestore } from 'services/firebase/client';
import { hasActiveSubscriptionInList } from 'utils/subscriptionStatus';
import { IAddress } from 'utils/types/User';
import { ISubscription } from 'utils/types/Subscription';

export interface ISelectedPlan {
  type: string;
  price: string;
  originalPrice: string;
  label: string;
  quantity?: number;
  total?: number;
  frete?: number;
  subtotal?: number;
  pack?: string;
  copo?: boolean;
  subscriptionID: string;
}

export interface IPersonalData {
  fullName: string;
  cpf: string;
  phoneNumber: string;
  day: string;
  month: string;
  year: string;
  cep: string;
  address: IAddress;
  number: string;
  complement: string;
  reference: string;
  city: string;
  email: string;
  uf: string;
  neighborhood: string;
  subscriptions?: ISubscription[];
  referralCode?: string;
  referredBy?: string;
  usedPromoCode?: string;
  promoExtraBeers?: number;
  promoExtraBeersMonths?: number;
}

interface ShippingOption {
  id: string;
  price: number;
  description: string;
}

interface SubscriptionContextType {
  selectedPlan: ISelectedPlan | null;
  personalData: IPersonalData;
  shippingOption: ShippingOption | null;
  usedReferralCode: string;
  loading: boolean;
  hasActiveSubscription: boolean;
  setSelectedPlan: (plan: ISelectedPlan) => void;
  setPersonalData: (data: IPersonalData) => void;
  updatePersonalData: (data: Partial<IPersonalData>) => void;
  updateSubscription: (plan: ISelectedPlan) => void;
  setShippingOption: (option: ShippingOption | null) => void;
  setUsedReferralCode: (code: string) => void;
}

const defaultPersonalData: IPersonalData = {
  fullName: '',
  cpf: '',
  phoneNumber: '',
  day: '',
  month: '',
  year: '',
  cep: '',
  address: {
    street: '',
    neighborhood: '',
    city: '',
    zip: '',
    number: '',
    complement: '',
    reference: '',
    recipient: '',
    id: '',
    uf: '',
  },
  number: '',
  complement: '',
  reference: '',
  city: '',
  email: '',
  uf: '',
  neighborhood: '',
  subscriptions: [],
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined,
);

// Função para verificar se o usuário tem subscription ativa
const checkHasActiveSubscription = (
  subscriptions?: ISubscription[],
): boolean => hasActiveSubscriptionInList(subscriptions);

export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [selectedPlan, setSelectedPlan] = useState<ISelectedPlan | null>(null);
  const [personalData, setPersonalData] =
    useState<IPersonalData>(defaultPersonalData);

  const [shippingOption, setShippingOption] = useState<{
    id: string;
    price: number;
    description: string;
  } | null>(null);
  const [usedReferralCode, setUsedReferralCode] = useState('');
  const [loading, setLoading] = useState<boolean>(true);

  // Calcular se tem subscription ativa
  const hasActiveSubscription = checkHasActiveSubscription(
    personalData.subscriptions,
  );

  // ✅ Fetch user data from Firestore when logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async user => {
      if (user) {
        if (user.isAnonymous) {
          setPersonalData(defaultPersonalData);
          setLoading(false);
          return;
        }

        setLoading(true);

        try {
          const userDoc = await getDoc(
            doc(firestore, 'subscription_users', user.uid),
          );

          if (userDoc.exists()) {
            const userData = userDoc.data() as IPersonalData;
            setPersonalData(userData);
          } else {
            // Check if we have pending data in localStorage from SubscriptionPersonalForm
            const pendingData = localStorage.getItem('pendingUserData');
            if (pendingData) {
              try {
                const userData = JSON.parse(pendingData) as IPersonalData;
                await setDoc(doc(firestore, 'subscription_users', user.uid), {
                  ...userData,
                  createdAt: new Date().toISOString(),
                });
                localStorage.removeItem('pendingUserData'); // Clean up
                setPersonalData(userData);
              } catch (saveError) {
                console.error('Error saving pending data:', saveError);
                setPersonalData(defaultPersonalData);
              }
            } else {
              // Conta Auth sem perfil (ex.: cashback de growlers): cria doc mínimo
              const seeded: IPersonalData = {
                ...defaultPersonalData,
                email: user.email || '',
                fullName:
                  user.displayName ||
                  (user.email ? user.email.split('@')[0] : ''),
              };
              try {
                await setDoc(doc(firestore, 'subscription_users', user.uid), {
                  ...seeded,
                  id: user.uid,
                  source: 'auth_profile_seed',
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString(),
                });
                setPersonalData(seeded);
              } catch (seedError) {
                console.error('Error seeding user profile:', seedError);
                setPersonalData(seeded);
              }
            }
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setPersonalData(defaultPersonalData);
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, []);

  const updateSubscription = (plan: ISelectedPlan): void => {
    setSelectedPlan(prev => ({
      ...prev,
      ...plan,
    }));
  };

  const updatePersonalData = (data: Partial<IPersonalData>): void => {
    setPersonalData(prev => ({
      ...prev,
      ...data,
    }));
  };

  const value = React.useMemo(
    () => ({
      selectedPlan,
      personalData,
      shippingOption,
      usedReferralCode,
      loading,
      hasActiveSubscription,
      setSelectedPlan,
      setPersonalData,
      updatePersonalData,
      setShippingOption,
      setUsedReferralCode,
      updateSubscription,
    }),
    [
      selectedPlan,
      personalData,
      shippingOption,
      usedReferralCode,
      loading,
      hasActiveSubscription,
    ],
  );

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      'useSubscription must be used within a SubscriptionProvider',
    );
  }
  return context;
};
